import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import MusicShell from "../fanHome/MusicShell";
import { isSignedIn } from "../fanHome/fanHomeApi";
import { ACCENT, PrimaryButton } from "../artistStudio/shared";
import { CounterNoticeForm } from "./CaseDialog";
import { claimErrorText, formatDay, rightsClaimApi } from "./rightsClaimApi";

// /rights/counter-notice (mobile design, screen 42): answer a copyright notice filed against your sound. The page lists the
// notices you can still answer (rights API `can_counter`), you pick one, then send the counter-notice. Sending it starts the
// restoration timer the rights team follows; the case then shows under My cases.
export default function CounterNoticePage() {
  const [state, setState] = useState({ loading: true, items: [], error: "" });
  const [picked, setPicked] = useState(null);
  const [sent, setSent] = useState(false);

  const load = useCallback(() => {
    setState((s) => ({ ...s, loading: true, error: "" }));
    rightsClaimApi.mine({ limit: 50 }).then(
      (data) => setState({ loading: false, items: (data?.items || []).filter((i) => i.can_counter), error: "" }),
      (e) => setState({ loading: false, items: [], error: claimErrorText(e, "We couldn't load your notices.") })
    );
  }, []);
  useEffect(() => { if (isSignedIn()) load(); }, [load]);

  return (
    <MusicShell maxWidth="max-w-2xl">
      <Link to="/rights" className="text-sm font-semibold hover:underline" style={{ color: ACCENT }}>← Rights &amp; legal</Link>
      <h1 className="mt-3 text-2xl md:text-3xl font-bold text-gray-900">Counter-notification</h1>
      <p className="mt-1 text-gray-600">Use this if a copyright notice was filed against your sound by mistake, or you have the right to use the work.</p>
      {!isSignedIn() ? (
        <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-5" data-testid="counter-login">
          <p className="text-gray-700">Sign in to answer a notice.</p>
          <Link to="/login" className="mt-4 inline-block rounded-full px-5 py-2.5 font-semibold text-white" style={{ backgroundColor: ACCENT }}>Log in</Link>
        </div>
      ) : sent ? (
        <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-5 text-center" data-testid="counter-sent">
          <h2 className="text-xl font-bold text-gray-900">Your counter-notice was sent</h2>
          <p className="mt-2 text-sm text-gray-600">Our rights team will review it. You can follow it under My cases.</p>
          <Link to="/rights-cases" className="mt-5 block rounded-full py-3 text-center font-semibold text-white" style={{ backgroundColor: ACCENT }}>View my cases</Link>
        </div>
      ) : picked ? (
        <div className="mt-6" data-testid="counter-pick-form">
          <p className="text-sm text-gray-600">Answering <span className="font-semibold text-gray-900">{picked.case_number || `notice #${picked.id}`}</span>{picked.track?.title ? ` about “${picked.track.title}”` : ""}. <button type="button" onClick={() => setPicked(null)} className="ml-1 underline" style={{ color: ACCENT }}>Choose another</button></p>
          <CounterNoticeForm caseId={picked.id} onDone={() => setSent(true)} />
        </div>
      ) : (
        <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-5" data-testid="counter-list">
          {state.loading && <p className="text-gray-500">Loading…</p>}
          {state.error && <p className="text-red-600" role="alert">{state.error} <button type="button" className="underline font-semibold" onClick={load}>Try again</button></p>}
          {!state.loading && !state.error && state.items.length === 0 && (
            <p className="text-gray-700">You have no copyright notices to answer right now. Notices filed about your sounds appear here, and under <Link to="/rights-cases" className="font-semibold underline" style={{ color: ACCENT }}>My cases</Link>.</p>
          )}
          {!state.loading && !state.error && state.items.length > 0 && (
            <>
              <p className="mb-3 text-sm font-semibold text-gray-900">Choose the notice you want to answer</p>
              <ul className="space-y-3">
                {state.items.map((i) => (
                  <li key={i.id} className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 p-3" data-testid={`counter-item-${i.id}`}>
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-gray-900">{i.track?.title || (i.track?.id ? `Sound #${i.track.id}` : "Sound")}</p>
                      <p className="text-xs text-gray-600">{i.case_number || `#${i.id}`} · received {formatDay(i.received_at)}</p>
                    </div>
                    <PrimaryButton onClick={() => setPicked(i)} className="!py-2 text-sm shrink-0">Answer</PrimaryButton>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </MusicShell>
  );
}
